import type { Storyboard } from "./director";
import type { Beat } from "./director";
import { clamp01 } from "../core/easing";
import { cutIn, cutOut } from "./anim";

/**
 * Anivaze v4 — film timeline & transitions.
 *
 * Turns a storyboard's beats into ONE continuous timeline where adjacent beats
 * OVERLAP by a short window, so they cross-fade (or push) into each other instead
 * of playing as disjoint clips. Pure/deterministic: `layersAt(t)` fully describes
 * what's on screen at any time, so the live player and the exporter agree.
 * See PLAN §7 (scene transitions).
 */

export type TransitionKind = "fade" | "push";

interface Win {
  beat: Beat;
  index: number;
  start: number;
  end: number;
  ovLeft: number; // overlap shared with the previous beat
  ovRight: number; // overlap shared with the next beat
}

export interface FilmTimeline {
  total: number;
  windows: Win[];
}

export interface FilmLayer {
  beat: Beat;
  index: number;
  localTime: number;
  opacity: number;
  /** horizontal offset as a fraction of frame width (for "push"). */
  dx: number;
  /** layer scale — the incoming beat "camera-settles" from 1.02 → 1. */
  scale: number;
}

const DEFAULT_OVERLAP = 0.5;

/** Build the overlapped film timeline. Overlap is capped per-pair so short beats stay valid. */
export function buildFilmTimeline(sb: Storyboard, overlap = DEFAULT_OVERLAP): FilmTimeline {
  const beats = sb.beats;
  const windows: Win[] = [];
  let start = 0;
  for (let i = 0; i < beats.length; i++) {
    const dur = beats[i].dur;
    let ovLeft = 0;
    if (i > 0) {
      ovLeft = Math.min(overlap, beats[i - 1].dur * 0.5, dur * 0.5);
      start = windows[i - 1].start + beats[i - 1].dur - ovLeft;
      windows[i - 1].ovRight = ovLeft;
    }
    windows.push({ beat: beats[i], index: i, start, end: start + dur, ovLeft, ovRight: 0 });
  }
  const total = windows.length ? windows[windows.length - 1].end : 0;
  return { total, windows };
}

export const filmDuration = (sb: Storyboard): number => buildFilmTimeline(sb).total;

const PUSH = 0.16;

/**
 * What's on screen at global time `t` (1 layer normally, 2 during a transition).
 * The cut is ASYMMETRIC, like an editor cuts film: the incoming beat attacks
 * fast and lands softly (cutIn) while settling from 1.02 → 1 scale (a camera
 * settle); the outgoing beat lingers then releases (cutOut). A symmetric linear
 * crossfade is the "slideshow" tell this replaces.
 */
export function layersAt(timeline: FilmTimeline, t: number, kind: TransitionKind = "fade"): FilmLayer[] {
  const out: FilmLayer[] = [];
  for (const w of timeline.windows) {
    if (t < w.start || t >= w.end) continue;
    const inP = w.ovLeft > 0 ? clamp01((t - w.start) / w.ovLeft) : 1;
    const outP = w.ovRight > 0 ? clamp01((w.end - t) / w.ovRight) : 1;
    const entering = inP < 1;
    const leaving = outP < 1;
    const opacity = Math.min(entering ? cutIn(inP) : 1, leaving ? cutOut(outP) : 1);
    let dx = 0;
    if (kind === "push") {
      if (entering) dx = (1 - cutIn(inP)) * PUSH; // entering from the right
      else if (leaving) dx = -(1 - cutOut(outP)) * PUSH; // leaving to the left
    }
    const scale = entering ? 1.02 - 0.02 * cutIn(inP) : 1;
    out.push({ beat: w.beat, index: w.index, localTime: Math.max(0, t - w.start), opacity, dx, scale });
  }
  return out;
}

/** Index of the "primary" beat at time t (last window that has started). */
export function primaryIndexAt(timeline: FilmTimeline, t: number): number {
  let idx = 0;
  for (const w of timeline.windows) if (t >= w.start) idx = w.index;
  return idx;
}

/** A good time to park the playhead to preview beat `i` (its content settled). */
export function seekTimeFor(timeline: FilmTimeline, i: number): number {
  const w = timeline.windows[Math.max(0, Math.min(i, timeline.windows.length - 1))];
  if (!w) return 0;
  return w.start + Math.min(1.3, (w.end - w.start) * 0.5);
}
